import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Camera, CheckCircle2, Swords } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const inputCls = "w-full h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8] bg-white";
const EMPTY = { full_name: "", dob: "", address: "", postcode: "", mobile: "", email: "", nok_name: "", nok_phone: "", screening: [], screening_other: "", allergies: "", medications: "", pmh: [], pmh_other: "", nhs_number: "", insurance: "", lives_in_uk: true, corner: "", declaration: false };

export function FighterFormEvent() {
  const { token } = useParams();
  return <FighterForm eventToken={token} />;
}

function Shell({ title, subtitle, error, children }) {
  return (
    <div className="min-h-screen bg-[#F4F7FA]" data-testid="fighter-form-page">
      <div className="bg-[#0F2547] px-4 py-6 text-center">
        <Swords className="h-8 w-8 text-[#7dd3fc] mx-auto mb-2" />
        <h1 className="text-white font-black text-xl sm:text-2xl uppercase tracking-wide">Wales Event Medical Services</h1>
        <p className="text-[#7dd3fc] text-xs sm:text-sm mt-1 font-semibold">Pre-fight medical form{title ? ` — ${title}` : ""}</p>
        {subtitle && <p className="text-slate-300 text-xs mt-1">{subtitle}</p>}
      </div>
      <div className="max-w-lg mx-auto p-4 sm:p-6">
        {error ? (
          <div className="bg-white rounded-2xl border border-red-200 p-8 text-center">
            <p className="text-sm text-[#D5281B] font-semibold" data-testid="form-error">{error}</p>
          </div>
        ) : children}
      </div>
    </div>
  );
}

export default function FighterForm({ eventToken = "" }) {
  const { token: paramToken } = useParams();
  const token = paramToken || "";
  const isEvent = !!eventToken;
  const base = isEvent ? `/public/fight-form-event/${eventToken}` : `/public/fighter-form/${token}`;
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get(base)
      .then((r) => {
        setInfo(r.data);
        if (!isEvent) {
          setForm((f) => ({ ...f, full_name: r.data.prefill.full_name || f.full_name, email: r.data.prefill.email || f.email, mobile: r.data.prefill.mobile || f.mobile }));
        }
      })
      .catch((e) => setError(formatApiError(e)));
  }, [base, isEvent]);

  useEffect(() => { load(); }, [load]);

  const toggle = (key, val) => setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));

  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!photo && !info?.has_headshot) { window.scrollTo(0, 0); return; }
    setSaving(true);
    try {
      const res = await api.post(base, form);
      if (photo) {
        const headshotToken = isEvent ? res.data.form_token : token;
        const fd = new FormData();
        fd.append("file", photo);
        await api.post(`/public/fighter-form/${headshotToken}/headshot`, fd);
      }
      setDone(true);
      window.scrollTo(0, 0);
    } catch (err) {
      window.dispatchEvent(new Event("fighter-form-error"));
      alert(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <Shell title={info?.event}>
        <div className="bg-white rounded-2xl border border-emerald-200 p-8 text-center" data-testid="form-success">
          <CheckCircle2 className="h-10 w-10 text-[#007F3B] mx-auto mb-3" />
          <h2 className="font-bold text-lg text-[#0F2547]">Form received — thank you</h2>
          <p className="text-sm text-slate-500 mt-2">See the medical team when you arrive for your short pre-fight check. No form, no fight.</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title={info?.event} subtitle={info ? `${info.event_date || ""} ${info.venue ? `· ${info.venue}` : ""}` : ""} error={error}>
      {info && (
        <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 space-y-4" data-testid="fighter-form">
          {!isEvent && info.submitted && (
            <p className="text-xs font-bold text-[#007F3B] bg-emerald-50 rounded-lg px-3 py-2">You've already submitted — update anything below and resubmit if it's changed.</p>
          )}
          <p className="text-xs text-slate-500">This form must be completed by all competitors before the event. It is required for medical assessment, event clearance and your safety.</p>

          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Headshot photo *</p>
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="headshot-upload-btn"
              className="w-full rounded-xl border-2 border-dashed border-slate-300 hover:border-[#005EB8] transition-colors p-4 flex items-center gap-3">
              {photoPreview ? (
                <img src={photoPreview} alt="Headshot" className="h-16 w-16 rounded-full object-cover" />
              ) : (
                <span className="h-16 w-16 rounded-full bg-slate-100 grid place-items-center"><Camera className="h-6 w-6 text-slate-400" /></span>
              )}
              <span className="text-sm font-semibold text-slate-600 text-left">
                {photo || info.has_headshot ? "Photo chosen — tap to change" : "Tap to upload a clear headshot (face photo)"}
                {info.has_headshot && !photo && <span className="block text-[11px] text-[#007F3B] font-bold">We already have your photo</span>}
              </span>
            </button>
            <input ref={fileRef} type="file" accept="image/*" capture="user" onChange={pickPhoto} className="hidden" data-testid="headshot-file-input" />
          </div>

          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Your details</p>
            <input required placeholder="Full name *" value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} data-testid="ff-full-name" className={inputCls} />
            <label className="block">
              <span className="text-[11px] font-semibold text-slate-500 mb-0.5 block">Date of birth *</span>
              <input required type="date" value={form.dob} onChange={(e) => setForm((f) => ({ ...f, dob: e.target.value }))} data-testid="ff-dob" className={inputCls} />
            </label>
            <input required placeholder="Address *" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} data-testid="ff-address" className={inputCls} />
            <input required placeholder="Post code *" value={form.postcode} onChange={(e) => setForm((f) => ({ ...f, postcode: e.target.value }))} data-testid="ff-postcode" className={inputCls} />
            <input placeholder="NHS number (optional)" value={form.nhs_number} onChange={(e) => setForm((f) => ({ ...f, nhs_number: e.target.value }))} data-testid="ff-nhs" className={inputCls} />
            <input required type="tel" placeholder="Mobile number *" value={form.mobile} onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))} data-testid="ff-mobile" className={inputCls} />
            <input required type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="ff-email" className={inputCls} />
          </div>

          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Emergency contact</p>
            <input required placeholder="Next of kin *" value={form.nok_name} onChange={(e) => setForm((f) => ({ ...f, nok_name: e.target.value }))} data-testid="ff-nok-name" className={inputCls} />
            <input required type="tel" placeholder="Next of kin's phone number *" value={form.nok_phone} onChange={(e) => setForm((f) => ({ ...f, nok_phone: e.target.value }))} data-testid="ff-nok-phone" className={inputCls} />
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Have you had any of the following?</p>
            <div className="space-y-1.5">
              {info.screening_options.map((o) => (
                <label key={o} className="flex items-start gap-2.5 rounded-lg border border-slate-200 px-3 py-2.5 cursor-pointer">
                  <input type="checkbox" checked={form.screening.includes(o)} onChange={() => toggle("screening", o)} data-testid={`ff-screening-${o.slice(0, 20).replace(/\W/g, "-")}`} className="h-4 w-4 mt-0.5 accent-[#005EB8]" />
                  <span className="text-sm text-slate-700">{o}</span>
                </label>
              ))}
            </div>
            <input placeholder="Any other signs & symptoms you're concerned about?" value={form.screening_other}
              onChange={(e) => setForm((f) => ({ ...f, screening_other: e.target.value }))} data-testid="ff-screening-other" className={`${inputCls} mt-2`} />
          </div>

          <input required placeholder="Allergies? * (write 'None' if none)" value={form.allergies} onChange={(e) => setForm((f) => ({ ...f, allergies: e.target.value }))} data-testid="ff-allergies" className={inputCls} />
          <input required placeholder="Medications? * (write 'None' if none)" value={form.medications} onChange={(e) => setForm((f) => ({ ...f, medications: e.target.value }))} data-testid="ff-medications" className={inputCls} />

          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Past medical history</p>
            <div className="space-y-1.5">
              {info.pmh_options.map((o) => (
                <label key={o} className="flex items-start gap-2.5 rounded-lg border border-slate-200 px-3 py-2.5 cursor-pointer">
                  <input type="checkbox" checked={form.pmh.includes(o)} onChange={() => toggle("pmh", o)} data-testid={`ff-pmh-${o.slice(0, 20).replace(/\W/g, "-")}`} className="h-4 w-4 mt-0.5 accent-[#005EB8]" />
                  <span className="text-sm text-slate-700">{o}</span>
                </label>
              ))}
            </div>
            <input placeholder="Any other past medical history" value={form.pmh_other}
              onChange={(e) => setForm((f) => ({ ...f, pmh_other: e.target.value }))} data-testid="ff-pmh-other" className={`${inputCls} mt-2`} />
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Do you live in the UK? *</p>
            <div className="grid grid-cols-2 gap-2">
              {[[true, "Yes"], [false, "No"]].map(([v, label]) => (
                <button type="button" key={label} onClick={() => setForm((f) => ({ ...f, lives_in_uk: v }))} data-testid={`ff-uk-${label.toLowerCase()}`}
                  className={`h-12 rounded-lg border-2 text-sm font-bold transition-colors ${form.lives_in_uk === v ? "border-[#005EB8] bg-sky-50 text-[#005EB8]" : "border-slate-200 text-slate-500"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {!form.lives_in_uk && (
            <textarea rows={2} placeholder="Medical / travel insurance details (insurer, policy number) — if you have any" value={form.insurance}
              onChange={(e) => setForm((f) => ({ ...f, insurance: e.target.value }))}
              data-testid="ff-insurance" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
          )}

          {!isEvent && info.corner ? (
            <p className={`text-sm font-black uppercase tracking-widest rounded-lg px-3 py-3 text-center ${info.corner === "red" ? "bg-red-50 text-red-600" : "bg-sky-50 text-[#005EB8]"}`} data-testid="ff-corner-info">
              You're fighting in the {info.corner} corner
            </p>
          ) : null}

          <label className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-[#F4F7FA] px-3 py-3 cursor-pointer">
            <input required type="checkbox" checked={form.declaration} onChange={(e) => setForm((f) => ({ ...f, declaration: e.target.checked }))} data-testid="ff-declaration" className="h-5 w-5 mt-0.5 accent-[#007F3B]" />
            <span className="text-xs text-slate-600">
              I have answered the above questions about my medical background and health truthfully and have not omitted any significant medical illness / injuries that I know of that would prevent me partaking in this event. I authorise the promoters or their representatives to treat me should I need medical intervention at any time during this event.
            </span>
          </label>
          <p className="text-[11px] text-slate-400">By submitting this form, you consent to: pre-event medical screening and assessment, on-site medical evaluation where required, emergency treatment and referral if clinically indicated, and communication between authorised medical staff for continuity of care.</p>

          <button type="submit" disabled={saving || (!photo && !info.has_headshot)} data-testid="ff-submit"
            className="w-full h-13 py-3.5 rounded-lg bg-[#005EB8] hover:bg-[#004a93] disabled:opacity-50 transition-colors text-white text-sm font-bold">
            {saving ? "Submitting…" : (!photo && !info.has_headshot) ? "Add your headshot photo to submit" : "Submit medical form"}
          </button>
        </form>
      )}
    </Shell>
  );
}
